import React, { useState, useEffect } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  TextField,
  Grid,
  Icon,
  CircularProgress,
} from "@mui/material";
import {
  Search as SearchIcon,
  Refresh as RefreshIcon,
} from "@mui/icons-material";
import { useSnackbar } from "notistack";
import { Table as CustomTable } from "app/components";
import { useDispatch, useSelector } from "react-redux";
import {
  getMeeshoStatisticsStatusList,
} from "app/redux/slice/meeshoOrderManagementSlice";
import { usePagination } from "app/hooks/usePagination";
import Loader from "@jumbo/components/Loader";
import { useNavigate, useParams } from "react-router-dom";
import { LocalizationProvider, DateRangePicker } from "@mui/lab";
import AdapterDateFns from "@mui/lab/AdapterDateFns";
import { useLocation } from "react-router-dom";
import moment from "moment";
import { orderManagementStatisticsList } from "app/routes/constant";

const OrderStatisticsList = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const { status } = useParams();
  const location = useLocation();
  const { fromDate, toDate } = location.state || {};

  const meeshoData = useSelector((state) => state?.meeshoOrderManagement);
  const orders = meeshoData?.statusList?.list || [];

  const [searchTerm, setSearchTerm] = useState("");
  const [order, setOrder] = useState("desc");
  const [orderBy, setOrderBy] = useState("");
  const [dateRange, setDateRange] = useState([
    fromDate || null,
    toDate || null,
  ]);

  const handleDateChange = (newDateRange) => {
    setDateRange(newDateRange);
  };

  const { page, rowsPerPage, handleChangePage, handleChangeRowsPerPage } =
    usePagination({ id: "order-management-statistics-list" });

  useEffect(() => {
    if (status) {
      fetchOrders(status);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, rowsPerPage, dispatch, searchTerm, order, orderBy, status]);

  const fetchOrders = async (id, clear) => {
    const params = {
      page: page,
      perPage: rowsPerPage,
      //   search: searchTerm.trim(),
    };
    if (orderBy !== "") {
      params.sort = order === "asc" ? 1 : -1;
      params.field = orderBy;
    }
    if (dateRange[0] !== null || dateRange[1] !== null) {
      if (!clear) {
        params.fromDate = moment(dateRange[0]).format("YYYY-MM-DD");
        params.toDate = moment(dateRange[1]).format("YYYY-MM-DD");
      }
    }

    dispatch(getMeeshoStatisticsStatusList({ id: id, data: params }));
  };

  const handleApplyFilter = () => {
    const params = {
      page: page,
      perPage: rowsPerPage,
    };
    if (orderBy !== "") {
      params.sort = order === "asc" ? 1 : -1;
      params.field = orderBy;
    }
    if (dateRange[0] === null || dateRange[1] === null) {
      enqueueSnackbar("Please select both start and end date", {
        variant: "error",
      });
      return;
    }

    params.fromDate = moment(dateRange[0]).format("YYYY-MM-DD");
    params.toDate = moment(dateRange[1]).format("YYYY-MM-DD");
    navigate(orderManagementStatisticsList(status), {
      state: null,
    });
    dispatch(getMeeshoStatisticsStatusList({ id: status, data: params }), {});
  };

  const columns = [
    {
      id: "productName",
      label: "Product Name",
      sort: true,
    },
    {
      id: "sku",
      label: "SKU",
      sort: false,
    },
    {
      id: "count",
      label: "Count",
      sort: true,
    },
  ];

  const sortData = (Order, OrderBy) => {
    setOrder(Order);
    setOrderBy(OrderBy);
  };

  const handleSearch = (event) => {
    setSearchTerm(event.target.value);
    handleChangePage(0, 0);
  };

  return (
    <Box>
      <Typography variant="h4" component="h1" gutterBottom>
        Product List
      </Typography>

      {/* Search and Filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid
            container
            spacing={2}
            alignItems="center"
            justifyContent="center"
          >
            <Grid item xs={12} md={3}>
              {/* <TextField
                fullWidth
                size="small"
                placeholder="Search orders..."
                value={searchTerm}
                onChange={handleSearch}
                InputProps={{
                  startAdornment: (
                    <SearchIcon sx={{ mr: 1, color: "text.secondary" }} />
                  ),
                }}
              /> */}
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DateRangePicker
                  startText="From Date - To Date"
                  endText="To Date"
                  inputFormat="dd/MM/yyyy"
                  value={dateRange}
                  onChange={handleDateChange}
                  renderInput={({ inputProps, ...startProps }, endProps) => {
                    const startValue = inputProps.value;
                    delete inputProps.value;
                    return (
                      <TextField
                        {...startProps}
                        inputProps={inputProps}
                        value={`${startValue || "DD/MM/YYYY"} - ${
                          endProps.inputProps.value || "DD/MM/YYYY"
                        }`}
                        fullWidth
                        size="small"
                      />
                    );
                  }}
                />
              </LocalizationProvider>
            </Grid>
            <Grid item xs={12} sm={2}>
              <Button
                variant="contained"
                onClick={handleApplyFilter}
                disabled={meeshoData?.statusList?.loading}
                startIcon={
                  meeshoData?.statusList?.loading ? (
                    <CircularProgress size={16} />
                  ) : (
                    ""
                  )
                }
                fullWidth
              >
                Apply
              </Button>
            </Grid>
            <Grid item xs={12} md={2}>
              <Button
                variant="outlined"
                onClick={() => {
                  setSearchTerm("");
                  handleChangePage(0, 0);
                  setDateRange([null, null]);
                  navigate(orderManagementStatisticsList(status), {
                    state: null,
                  });
                  fetchOrders(status, true);
                }}
                startIcon={<RefreshIcon />}
                fullWidth
              >
                Clear
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Orders Table */}
      {meeshoData?.statusList?.loading ? (
        <Loader />
      ) : (
        <Card>
          <CardContent>
            <CustomTable
              columns={columns}
              rows={orders}
              page={page}
              rowsPerPage={rowsPerPage}
              count={meeshoData?.statusList?.total || 0}
              Order={order}
              OrderBy={orderBy}
              sortData={sortData}
              handleChangePage={handleChangePage}
              handleChangeRowsPerPage={handleChangeRowsPerPage}
              loading={meeshoData?.statusList?.loading}
            />
          </CardContent>
        </Card>
      )}
    </Box>
  );
};

export default OrderStatisticsList;
